import { forEachInRadius, falloff, laplacian, LineBuffer, tint, clamp } from "../shared";

class Matter {
  constructor(ctx) { this.ctx = ctx; this.writesColors = true; this.line = new LineBuffer(ctx.overlay, 2, 1); this.alloc(); }
  alloc() { const n = this.ctx.count; this.z = new Float32Array(n); this.vz = new Float32Array(n); this.z0 = new Float32Array(n); this.L = new Float32Array(n); }
  resize() { this.alloc(); }
  brushRadius() { return this.ctx.params.tool === "fold" ? null : this.ctx.params.radius; }
  pointer(e) {
    const P = this.ctx.params;
    if (e.type === "down") { this.hold = e; if (P.tool === "fold") this.fold = { ax: e.x, az: e.z, bx: e.x, bz: e.z }; }
    else if (e.type === "move" && this.hold) { this.hold = e; if (this.fold) { this.fold.bx = e.x; this.fold.bz = e.z; } }
    else if (e.type === "up") { if (this.fold) this.crease(); this.hold = null; this.fold = null; }
  }
  crease() {
    const f = this.fold, dx = f.bx - f.ax, dz = f.bz - f.az, len = Math.hypot(dx, dz); if (len < 0.3) return;
    const nx = -dz / len, nz = dx / len, { N, px } = this.ctx, k = this.ctx.params.fold * 0.22;
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const s = (px[c] - f.ax) * nx + (px[r] - f.az) * nz;
      if (s > 0) { const i = r * N + c; this.z0[i] = clamp(this.z0[i] + s * k, -1, 1); this.vz[i] += 0.02; }
    }
  }
  step() {
    const P = this.ctx.params, { N, target, colors, px } = this.ctx, F = this.ctx.g.force, { z, vz, z0, L } = this;
    const subs = clamp(Math.round(2 * this.ctx.g.simSpeed), 1, 6);
    for (let s = 0; s < subs; s++) {
      for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) { const i = r * N + c; L[i] = laplacian(z, N, i, r, c); }
      if (this.hold && P.tool !== "fold") {
        const h = this.hold, dir = P.tool === "pull" ? 1 : -1;
        forEachInRadius(this.ctx, h.x, h.z, P.radius, (i, d, dx, dz) => {
          const w = falloff(d / P.radius, 0.6) * F * h.pressure;
          if (P.tool === "twist") z0[i] = clamp(z0[i] + 0.004 * w * Math.sin(2 * Math.atan2(dz, dx)), -1, 1);
          else vz[i] += dir * 0.012 * w;
        });
      }
      for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
        const i = r * N + c, edge = r === 0 || c === 0 || r === N - 1 || c === N - 1;
        if (P.gravity && edge) { z[i] = z0[i]; vz[i] = 0; continue; }
        const a = P.tension * L[i] - P.bend * laplacian(L, N, i, r, c) - P.damping * vz[i] + P.restore * (z0[i] - z[i]) - (P.gravity ? 0.0025 * F : 0);
        vz[i] += a; let nz = z[i] + vz[i];
        if (nz > 1 || nz < -1) { nz = clamp(nz, -1, 1); vz[i] = 0; }
        z[i] = nz; z0[i] += (nz - z0[i]) * P.memory;
      }
    }
    for (let i = 0; i < z.length; i++) { target[i] = z[i]; tint(colors, i, -Math.min(1, Math.abs(L[i]) * 14), 0.9); }
    const ln = this.line; ln.begin();
    if (this.fold) { const y = this.ctx.top + 0.2, f = this.fold; ln.seg(f.ax, y, f.az, f.bx, y, f.bz, 1, 0.72, 0.1, 0, 0.94, 1); }
    ln.end(); void px;
  }
  action(id) {
    const { N, px, half } = this.ctx, { z0, vz } = this; this.ctx.pushHistory();
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const i = r * N + c, u = px[c] / half, v = px[r] / half;
      if (id === "bend") z0[i] = clamp(1.4 * u * u - 0.6, -1, 1);
      if (id === "saddle") z0[i] = 0.75 * (u * u - v * v);
      if (id === "shock") vz[i] += 0.09 * Math.exp(-(u * u + v * v) * 30);
      if (id === "flatten") { z0[i] = 0; this.z[i] = 0; vz[i] = 0; }
    }
  }
  snapshot() { return { z: this.z.slice(), z0: this.z0.slice() }; }
  restore(s) { if (s.z.length !== this.z.length) return; this.z.set(s.z); this.z0.set(s.z0); this.vz.fill(0); }
  clear() { this.z.fill(0); this.z0.fill(0); this.vz.fill(0); }
  telemetry() {
    let mx = 0, e = 0; for (let i = 0; i < this.L.length; i++) { const a = Math.abs(this.L[i]); if (a > mx) mx = a; e += this.vz[i] ** 2; }
    return [["Tool", this.ctx.params.tool.toUpperCase()], ["Peak strain", mx.toFixed(3)], ["Kinetic", (e * 1000).toFixed(2)], ["Memory", this.ctx.params.memory > 0 ? "PLASTIC" : "ELASTIC"]];
  }
  dispose() { this.line.dispose(); }
}

const matter = {
  id: "programmable-matter", name: "Programmable Matter", icon: "Layers", rest: 0,
  desc: "A continuous elastic membrane with tension (stretch), bending stiffness and plastic memory. Push or pull it, twist it, or drag a line to fold it along a crease. Amber tint shows local strain.",
  math: "z̈ = T∇²z − B∇⁴z − γż + k(z₀ − z) + F",
  params: [
    { key: "tool", label: "Force tool", type: "select", options: [["push", "Push"], ["pull", "Pull"], ["fold", "Fold line"], ["twist", "Twist"]], def: "push" },
    { key: "radius", label: "Force radius", type: "range", min: 0.3, max: 3, step: 0.05, def: 1.1 },
    { key: "tension", label: "Stretch tension T", type: "range", min: 0, max: 0.3, step: 0.005, def: 0.12 },
    { key: "bend", label: "Bending stiffness B", type: "range", min: 0, max: 0.03, step: 0.001, def: 0.008 },
    { key: "damping", label: "Damping γ", type: "range", min: 0.005, max: 0.2, step: 0.005, def: 0.04 },
    { key: "restore", label: "Elastic recovery k", type: "range", min: 0, max: 0.1, step: 0.002, def: 0.01 },
    { key: "memory", label: "Plastic memory", type: "range", min: 0, max: 0.05, step: 0.001, def: 0.004 },
    { key: "fold", label: "Fold angle", type: "range", min: 0.2, max: 2, step: 0.05, def: 1 },
    { key: "gravity", label: "Gravity sag (pinned edges)", type: "toggle", def: false },
  ],
  actions: [{ id: "bend", label: "Bend cylinder" }, { id: "saddle", label: "Saddle bend" }, { id: "shock", label: "Shockwave" }, { id: "flatten", label: "Flatten" }],
  create: (ctx) => new Matter(ctx),
};
export default matter;
